/**
 * Search Controller
 * Handles debounced search filtering of applications
 */
export class SearchController {
  constructor(options = {}) {
    this.debounceMs = options.debounceMs || 150;
    this.onSearch = options.onSearch || (() => {});
    this.debounceTimer = null;
    this.searchInput = null;
    this.clearButton = null;
  }

  init() {
    this.searchInput = document.querySelector('.portal-search-input');
    this.clearButton = document.querySelector('.portal-search-clear');

    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', () => this._handleInput());
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clear();
      }
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clear());
    }

    this._bindKeyboardShortcuts();
  }

  _bindKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // '/' focuses the search input (unless already in an input/textarea)
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        this.searchInput.focus();
      }
      // Escape blurs the search input
      if (e.key === 'Escape' && document.activeElement === this.searchInput) {
        this.searchInput.blur();
      }
    });
  }

  _handleInput() {
    const query = this.searchInput.value.trim();

    // Update clear button visibility
    if (this.clearButton) {
      this.clearButton.classList.toggle('visible', query.length > 0);
    }

    // Debounce the search
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this._performSearch(query);
    }, this.debounceMs);
  }

  _performSearch(query) {
    const normalizedQuery = query.toLowerCase();
    const appCards = document.querySelectorAll('.app-card-wrapper[data-app-name]');
    let visibleCount = 0;

    appCards.forEach(card => {
      const appName = (card.dataset.appName || '').toLowerCase();
      const matches = !normalizedQuery || appName.includes(normalizedQuery);

      card.classList.toggle('search-hidden', !matches);
      if (matches) visibleCount++;
    });

    // Update section visibility
    this._updateSectionVisibility();

    const noResultsEl = document.querySelector('.portal-no-results');
    if (noResultsEl) {
      noResultsEl.classList.toggle('visible', visibleCount === 0 && query.length > 0);
    }

    this.onSearch(query, visibleCount);
  }

  _updateSectionVisibility() {
    const sections = document.querySelectorAll('.portal-section');
    sections.forEach(section => {
      const visibleCards = section.querySelectorAll('.app-card-wrapper:not(.search-hidden)');
      const hasVisibleCards = visibleCards.length > 0;

      // Don't hide section headers during search, just indicate emptiness
      const content = section.querySelector('.section-content');
      if (content) {
        const emptyState = content.querySelector('.portal-empty-state');
        if (emptyState) {
          // If it's a section that can have empty state (favorites), handle it
          emptyState.style.display = hasVisibleCards ? 'none' : '';
        }
      }
    });
  }

  clear() {
    if (this.searchInput) {
      this.searchInput.value = '';
      this._handleInput();
      this.searchInput.focus();
    }
  }
}
